import { CheckmarkCircle02Icon } from '@hugeicons/react'
import clsx from 'clsx'
import type { CSSProperties } from 'react'

import {
  COMPLETED_ACCENT,
  getResourceIconSpec,
  resolveResourceType,
  type ResourceIconContext,
  type ResourceIconSpec
} from '@/shared/resourceModuleParity'

import { EvnttzIcon } from '@/shared/ui/EvnttzIcons'

type ResourceModuleIconProps = {
  resourceType?: string | null
  /** `objectives` = HugeIcons em tile (padrão). `nav` = EvnttzIcon. */
  context?: ResourceIconContext
  size?: number
  /** Lado do quadrado em `tile`. Default: ~2.2× `size`. */
  tileSize?: number
  variant?: 'tile' | 'plain'
  completed?: boolean
  className?: string
  tileClassName?: string
  style?: CSSProperties
  tileStyle?: CSSProperties
}

function renderIconSpec(
  spec: ResourceIconSpec,
  size: number,
  options?: { strokeOnTile?: boolean }
) {
  if (spec.kind === 'evnttz') {
    return <EvnttzIcon name={spec.name} size={size} aria-hidden />
  }

  if (spec.kind === 'asset') {
    return (
      <img
        src={spec.src}
        alt=""
        aria-hidden
        width={size}
        height={size}
        className="pointer-events-none select-none object-contain"
        loading="lazy"
        decoding="async"
      />
    )
  }

  const { Icon } = spec
  return (
    <Icon
      size={size}
      className={options?.strokeOnTile ? 'text-white' : undefined}
      variant="stroke"
      aria-hidden
    />
  )
}

function tileBackground(
  accentColor: string,
  spec: ResourceIconSpec,
  context: ResourceIconContext
) {
  if (context === 'objectives' && spec.kind === 'huge') {
    return accentColor
  }

  if (spec.kind === 'evnttz' || spec.kind === 'asset') {
    // 0x18/0xFF ≈ 9,4% de opacidade. Antes era concatenação de alfa hex
    // (`${accentColor}18`), que só funciona quando o accent é um literal de 6
    // dígitos — com um token (`var(--color-brand)`) produzia CSS inválido e o
    // ladrilho perdia o fundo. `color-mix` aceita as duas formas.
    return `color-mix(in oklab, ${accentColor} 9.4%, transparent)`
  }

  return accentColor
}

export function ResourceModuleIcon({
  resourceType,
  context = 'objectives',
  size = 20,
  tileSize,
  variant = 'tile',
  completed = false,
  className,
  tileClassName,
  style,
  tileStyle
}: ResourceModuleIconProps) {
  const resolved = resolveResourceType(resourceType)
  const iconSpec = getResourceIconSpec(resourceType, context)
  const box = tileSize ?? Math.round(size * 2.2)

  if (completed) {
    return (
      <div
        className={clsx(
          'flex shrink-0 items-center justify-center rounded-lg',
          tileClassName,
          className
        )}
        style={{
          width: box,
          height: box,
          backgroundColor: COMPLETED_ACCENT,
          ...tileStyle,
          ...style
        }}
      >
        <CheckmarkCircle02Icon
          size={size}
          className="text-white"
          variant="stroke"
          aria-hidden
        />
      </div>
    )
  }

  if (variant === 'plain') {
    return (
      <span
        className={clsx('inline-flex shrink-0 items-center justify-center', className)}
        style={style}
      >
        {renderIconSpec(iconSpec, size)}
      </span>
    )
  }

  const iconSize = iconSpec.kind === 'evnttz' ? Math.round(size * 1.05) : size
  const strokeOnTile = context === 'objectives' && iconSpec.kind === 'huge'

  return (
    <div
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-lg',
        tileClassName,
        className
      )}
      style={{
        width: box,
        height: box,
        backgroundColor: tileBackground(resolved.accentColor, iconSpec, context),
        ...tileStyle,
        ...style
      }}
    >
      {renderIconSpec(iconSpec, iconSize, { strokeOnTile })}
    </div>
  )
}
